import type { BundleStatus } from '../../../Offer/types';
import { RichContent } from '../../../RichContent/RichContent';
import { StepProgressList } from '../../../StepProgressList/StepProgressList';
import type { Step } from '../../../StepProgressList/types';
import { Winning, type WinningProps } from '../../../Winning/Winning';
import type { OfferBundleActionHandlersMap } from '../../helpers/offerBundleActions';
import { hasPositiveAmount, shouldShowWinnings, type OfferGroup } from '../../helpers/offerHelpers';
import { getStepCode } from '../../utils/offerUtils';
import { OfferInlineButtons } from '../OfferInlineButtons/OfferInlineButtons';

type OfferContentProps = {
  offerBundleCode?: string;
  steps: Step[];
  stickyPanelContent?: React.ReactNode;
  termsContent: string;
  termsTitle: string;
  bundleStatus?: BundleStatus;
  winnings?: WinningProps['winnings'];
  showWinning?: boolean;
  namespace: string;
  offerType?: string;
  isMultipleOffers?: boolean;
  offerGroups?: OfferGroup[];
  showLoginButtons?: boolean;
  hasActiveMainStep?: boolean;
  offerActionHandlers?: OfferBundleActionHandlersMap;
};

function OfferContent({
  offerBundleCode,
  steps,
  stickyPanelContent,
  termsContent,
  termsTitle,
  bundleStatus,
  winnings,
  showWinning,
  namespace,
  offerType,
  isMultipleOffers = false,
  offerGroups = [],
  showLoginButtons = false,
  hasActiveMainStep = false,
  offerActionHandlers,
}: OfferContentProps) {
  const isOfferDisabled = bundleStatus === 'EXPIRED' || bundleStatus === 'CANCELLED';
  const stepListClassName = isOfferDisabled ? 'opacity-50' : undefined;
  const hasTerms = termsContent.trim().length > 0;

  return (
    <div className="mx-5 md:mx-8" data-testid="offer-content">
      {isMultipleOffers ? (
        <>
          <StepProgressList steps={steps} className={stepListClassName} isOfferDisabled={isOfferDisabled} namespace={namespace} bundleStatus={bundleStatus} offerType={offerType} />

          {offerGroups.map((offerGroup, groupIndex) => {
            const hasActiveStep = offerGroup.activeStepIndex !== null;
            const shouldShowInlineButtons = hasActiveStep && !hasActiveMainStep;

            const stepNumberOffset = 1 + offerGroups.slice(0, groupIndex).reduce((acc, og) => acc + og.steps.length, 0);

            let minimumAmountForBonus: number | undefined = undefined;
            if (offerGroup.currentAction === 'DEPOSIT' && offerGroup.offer.steps) {
              const depositStep = offerGroup.offer.steps.find(step => step.action === 'DEPOSIT');
              if (depositStep?.initialAmount?.fixed_amount) {
                minimumAmountForBonus = depositStep.initialAmount.fixed_amount;
              }
            }

            const handlerOfferData = {
              bundleCode: offerBundleCode,
              offerCode: offerGroup.offer.code,
              productIds: offerGroup.currentProducts,
              productURL: offerGroup.currentProductUrl,
              minimumAmountForBonus,
            };

            const offerActionHandler = offerActionHandlers![offerGroup.currentAction as keyof OfferBundleActionHandlersMap];

            const stepCode = getStepCode(offerGroup.currentAction, offerBundleCode, offerGroup.offer.code);

            return (
              <div key={offerGroup.offer.code}>
                {groupIndex >= 0 && (
                  <div className="relative">
                    <div className="border-t border-dashed border-border"></div>
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-0.5 bg-accent"></div>
                  </div>
                )}

                <div>
                  <StepProgressList
                    steps={offerGroup.steps}
                    className={stepListClassName}
                    isOfferDisabled={isOfferDisabled}
                    namespace={namespace}
                    bundleStatus={bundleStatus}
                    offerType={offerGroup.offer.type}
                    stepNumberOffset={stepNumberOffset}
                  />

                  {shouldShowInlineButtons && (
                    <OfferInlineButtons
                      shouldShowLoginButtons={showLoginButtons}
                      offerGroup={offerGroup}
                      onStepAction={() => offerActionHandler(handlerOfferData)}
                      buttonLoading={null}
                      stepCode={stepCode}
                      className="mt-4 mb-6"
                    />
                  )}

                  {shouldShowWinnings(offerGroup.offer, offerGroup.steps, bundleStatus) && (
                    <Winning winnings={offerGroup.offer.winnings} label="winnings.offer" namespace={namespace} className="mt-4 mb-6" />
                  )}
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <>
          <StepProgressList steps={steps} className={stepListClassName} isOfferDisabled={isOfferDisabled} namespace={namespace} bundleStatus={bundleStatus} offerType={offerType} />
          {showWinning && winnings && hasPositiveAmount(winnings) && <Winning winnings={winnings} label="winnings.offer" namespace={namespace} />}
        </>
      )}

      {stickyPanelContent && (
        <div className="md:my-10" data-testid="sticky-panel-container">
          {stickyPanelContent}
        </div>
      )}
      {hasTerms && (
        <div className="mt-8 px-1">
          <p className="paragraph-explanatory-strong mb-1" data-testid="terms-title">
            {termsTitle}
          </p>
          <RichContent
            information={termsContent}
            theme="quill"
            className="text-foreground-subtle [&_p]:!paragraph-s [&_ul]:!paragraph-s [&_ol]:!paragraph-s [&_li]:!paragraph-s [&_h1]:!paragraph-s [&_h2]:!paragraph-s [&_h3]:!paragraph-s [&_h4]:!paragraph-s [&_h5]:!paragraph-s [&_h6]:!paragraph-s [&_a]:text-foreground [&_a]:hover:text-foreground-hover"
            data-testid="terms-content"
          />
        </div>
      )}
    </div>
  );
}

export { OfferContent, type OfferContentProps };
